import React, { ChangeEvent, RefObject } from "react";
import { dateOfBirthValidator } from "utils";

// Interface for props of Input component
interface InputFieldProps {
  label: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  type: string;
  required: boolean;
  errorMessage?: string;
  name: string;
  maxLength?: number;
  inputRef?: RefObject<HTMLInputElement>;
  focusNext?: () => void; // Callback function to focus on the next input
  autoComplete?: string;
  disabled?: boolean;
  inputMode?: any;
}

// Input component
const Input: React.FC<InputFieldProps> = ({
  label,
  value,
  onChange,
  type,
  required,
  errorMessage,
  name,
  maxLength,
  inputRef,
  focusNext,
  autoComplete,
  disabled,
  inputMode,
}) => {
  // Function to handle input change events
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e);
    // Trigger focus on the next input when maxLength is reached
    if (maxLength && e.target.value?.length === maxLength && focusNext) {
      focusNext();
    }
  };

  // Function to set css class for invalid field
  const setClassForInvalidField = (maxLength: number | undefined, value: string, name: string, disabled: boolean | undefined) => {
    switch (name) {
      // Valid cases for specific fields
      case "customerNameFirst":
        return value.length > 1 ? "is-valid" : "is-invalid";
      case "customerNameLast":
        return value.length > 1 ? "is-valid" : "is-invalid";
      case "phoneNumber":
        return !value || (value.length > 0 && value.length < 14) ? "is-invalid" : "is-valid";
      case "dateOfBirth":
        const dobIsValid = dateOfBirthValidator(value);
        return dobIsValid ? "is-valid" : "is-invalid";
      case "customerSSN":
        // If SSN field is disabled it means the customer passed the credit check bypass....
        // ...and we want the css class to show "is-valid"
        return (!value || (value.length > 0 && value.length < 11)) && !disabled ? "is-invalid" : "is-valid";
      case "otherAuthNameFirst":
        return value.length > 1 ? "is-valid" : "is-invalid";
      case "otherAuthNameLast":
        return value.length > 1 ? "is-valid" : "is-invalid";
      case "otherAuthSSNLast4":
        return !value || (value.length > 0 && value.length < 4) ? "is-invalid" : "is-valid";
      case "billingStreetNumber":
        return value.length > 0 ? "is-valid" : "is-invalid";
      case "billingStreetName":
        return value.length > 0 ? "is-valid" : "is-invalid";
      case "billingApartmentNumber":
        return "is-valid";
      case "billingPOBox":
        return "is-valid";
      case "billingCity":
        return value.length > 0 ? "is-valid" : "is-invalid";
      case "billingZipCode":
        let validClass;
        if (value.length > 0 && value.length < 5) {
          validClass = "is-invalid";
        } else if (value.length > 5 && value.length < 10) {
          validClass = "is-invalid";
        } else {
          validClass = "is-valid";
        }
        return validClass;
      case "emailAddress":
        //Tests to see if email address contains @ and .
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value) ? "is-valid" : "is-invalid";
      // Default case for other fields
      default:
        return "is-valid";
    }
  };

  const formFieldValidCheck = setClassForInvalidField(maxLength, value, name, disabled);

  return (
    <div className='input-group has-validation'>
      <div className={`form-floating ${setClassForInvalidField(maxLength, value, name, disabled)}`}>
        <input
          type={type}
          inputMode={inputMode}
          className={`form-control ${setClassForInvalidField(maxLength, value, name, disabled)}`}
          id={`${name}-input`}
          onChange={handleInputChange}
          placeholder={label}
          value={value}
          name={name}
          required={required}
          maxLength={maxLength}
          ref={inputRef}
          autoComplete={autoComplete}
          disabled={disabled}
          aria-required={required}
          aria-invalid={formFieldValidCheck === "is-invalid"}
          aria-describedby={errorMessage ? `${name}-error` : undefined}
          style={name === "billingStreetName" && value.includes("PO Box") ? { color: "#e9ecef" } : {}}
        />
        <label htmlFor={`${name}-input`}>{label}</label>
      </div>
      {/* Display error message for invalid fields */}
      {value?.length > 0 && formFieldValidCheck === "is-invalid" ? <div className='invalid-feedback'>{errorMessage}</div> : null}
    </div>
  );
};

export default Input;
